import { Fragment, useContext, useEffect, useState, useRef } from 'react';

import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

import { iPlanRoutes, nonCurriculumRoutes, routes } from '../constants/routes';
import CreditContext from '../context/credits/context';
import { Portal } from '../enums/portal.enum';
import {
  checkInAppBrowser,
  useInAppBrowser,
} from '../hooks/useCheckInAppBrowser';
import { useClickOutside } from '../hooks/useClickOutside';
import { useGetPremiumRoutes } from '../hooks/useGetPremiumRoutes';
import { usePortalRoutes } from '../hooks/usePortalRoutes';
import { useSidebarNavigation } from '../hooks/useSidebarNavigation';
import useUpdatePinnedApps from '../hooks/useUpdatePinnedApps';
import { MenuItemType } from '../types/menu-item';
import { signInUser } from '../utils/auth.utils';
import { handleSignOut } from '../utils/handleSignOut';
import { getFeaturePath } from '../utils/pathUtils';
import { getIsResponsePopulated } from '../utils/responsePopulatedUtil';
import { mapRouteToMenuItem } from '../utils/routeUtils';

import { Button } from './Button';
import CreditDisplay from './molecules/CreditDisplay';
import ExpandableSection from './molecules/ExpandableSection';
import ModalAlerts from './organisms/Modal/ModalAlerts';
import PopUpModal from './PopUpModal';
import { SignInBtn } from './SigninBtn';

interface MobileSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface FeatureSectionsProps {
  featurePath: string;
  features: MenuItemType[];
  nonCurriculumFeatures: MenuItemType[];
  pinnedApps: MenuItemType[];
  selectedPortal: Portal;
  onNavigate: (route: string, fromIPlanSection?: boolean) => void;
}

const FeatureSections = ({
  featurePath,
  features,
  nonCurriculumFeatures,
  pinnedApps,
  selectedPortal,
  onNavigate,
}: FeatureSectionsProps) => {
  const isLocalPortal = selectedPortal === Portal.PHILIPPINES;
  const premiumRoutes = useGetPremiumRoutes();
  const iPlanFeatures = iPlanRoutes.map((route) =>
    mapRouteToMenuItem(route, premiumRoutes),
  );

  return (
    <div className="h-0 flex-1 overflow-y-auto">
      <div className="flex h-full flex-col gap-2 text-sm">
        <ul className="mt-4 mb-32 flex flex-col gap-4 px-4">
          <Button
            className="mx-3 flex flex-row items-center justify-center gap-2 rounded-md bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-600"
            onClick={() => onNavigate(routes.TUTORIALS)}
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
            Tutorial
          </Button>
          <ExpandableSection
            allowHighlight={false}
            featurePath={featurePath}
            items={pinnedApps}
            pinnedApps={pinnedApps}
            title="Favorites"
            onNavigate={onNavigate}
          />
          <ExpandableSection
            featurePath={featurePath}
            items={features}
            pinnedApps={pinnedApps}
            title={`${selectedPortal} Curriculum`}
            onNavigate={onNavigate}
          />
          {isLocalPortal && (
            <ExpandableSection
              featurePath={featurePath}
              items={iPlanFeatures}
              pinnedApps={pinnedApps}
              title="IPlan (DepEd Region 7)"
              titleLink={routes.IPLAN}
              onNavigate={(route) => onNavigate(route, true)}
              onTitleClick={(route) => onNavigate(route, true)}
            />
          )}
          <ExpandableSection
            featurePath={featurePath}
            items={nonCurriculumFeatures}
            pinnedApps={pinnedApps}
            title="Other Features"
            onNavigate={onNavigate}
          />
        </ul>
      </div>
    </div>
  );
};

export default function MobileSidebar({
  isOpen,
  setIsOpen,
}: MobileSidebarProps) {
  const { selectedPortal, getPortalRoutes } = usePortalRoutes();
  const pinnedApps = useUpdatePinnedApps();
  const { data: session } = useSession();
  const user = session?.user;
  const isLoggedIn = !!user;
  const router = useRouter();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { context: creditContext } = useContext(CreditContext);
  const { userCredit, isLoading } = creditContext;
  const { popUpData, setPopUpData } = useInAppBrowser();

  const {
    handleNavigate,
    handleConfirm,
    showConfirmationModal,
    setShowConfirmationModal,
    openModal,
  } = useSidebarNavigation();

  const currentPath = router.pathname;
  const featurePath = getFeaturePath(currentPath);
  const premiumRoutes = useGetPremiumRoutes();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useClickOutside(sidebarRef, isOpen, () => setIsOpen(false));

  const handleSignInClick = () => {
    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    const { query } = router;
    const queryString = new URLSearchParams(
      query as Record<string, string>,
    ).toString();

    signInUser({
      callbackUrl: `${router.pathname}?${queryString}`,
    });
  };

  const handleSignOutClick = () => {
    if (!getIsResponsePopulated()) {
      handleSignOut();
    } else {
      openModal(routes.MAIN, handleSignOut);
    }
  };

  const features = isClient
    ? getPortalRoutes().map((route) => mapRouteToMenuItem(route, premiumRoutes))
    : [];
  const nonCurriculumFeatures = nonCurriculumRoutes.map((route) =>
    mapRouteToMenuItem(route, premiumRoutes),
  );

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 h-screen bg-black/40 transition-opacity duration-300 ease-in-out md:hidden',
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <div
        ref={sidebarRef}
        className={clsx(
          'absolute left-0 top-0 bottom-0 flex h-full w-5/6 flex-col justify-between bg-white transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex justify-between border-b border-gray-200 p-4">
          <div className="flex h-full items-center">
            <img
              alt="logo"
              height={40}
              src={`${process.env.NEXT_PUBLIC_GOOGLE_CDN}/images/logo.svg`}
              width={40}
            />
            <div className="ml-3 flex h-full flex-col justify-center text-left text-sm font-bold leading-none">
              <span>
                Lesson
                <br />
                Planner PH
              </span>
            </div>
          </div>
          <Button
            aria-label="Close sidebar"
            className="relative flex h-10 w-10 items-center justify-center rounded-md text-gray-600 hover:text-blue-600"
            onClick={() => setIsOpen(false)}
          >
            <XMarkIcon className="h-6 w-6" />
          </Button>
        </div>

        {isLoggedIn ? (
          <div className="flex flex-1 flex-col overflow-hidden border-b text-sm">
            <div className="flex-shrink-0">
              <div className="flex flex-col justify-between gap-2 bg-gray-100 px-6 py-2">
                <p className="font-medium text-gray-900">{user.name}</p>
                <small className="text-gray-600">{user.email}</small>
              </div>
              <div className="flex flex-row items-start justify-evenly p-4 shadow-sm md:hidden">
                <div className="flex flex-row items-center gap-4">
                  <CreditDisplay
                    className="flex-row justify-center"
                    isLoading={isLoading}
                    userCredit={userCredit}
                    onRedirect={handleNavigate}
                  />
                </div>
              </div>
            </div>
            <FeatureSections
              featurePath={featurePath}
              features={features}
              nonCurriculumFeatures={nonCurriculumFeatures}
              pinnedApps={pinnedApps}
              selectedPortal={selectedPortal}
              onNavigate={handleNavigate}
            />

            <div className="grid justify-items-stretch border-t border-gray-200 p-4">
              <Button
                className="justify-self-end border-b border-gray-500 px-1 py-2 text-start text-base font-medium leading-5"
                onClick={handleSignOutClick}
              >
                Sign Out
              </Button>
            </div>
          </div>
        ) : (
          <Fragment>
            <div className="flex w-full items-center justify-center gap-4 p-4">
              <SignInBtn handleSignIn={handleSignInClick} />
              <PopUpModal {...popUpData} setIsOpen={setPopUpData} />
            </div>

            <FeatureSections
              featurePath={featurePath}
              features={features}
              nonCurriculumFeatures={nonCurriculumFeatures}
              pinnedApps={pinnedApps}
              selectedPortal={selectedPortal}
              onNavigate={handleNavigate}
            />
          </Fragment>
        )}
        <ModalAlerts
          handleConfirmClose={handleConfirm}
          setShowConfirmationModal={setShowConfirmationModal}
          showConfirmationModal={showConfirmationModal}
        />
      </div>
    </div>
  );
}
